import React, { useState } from 'react';
import { Layers, Bookmark, Video, Info, Trash2, Edit3 } from 'lucide-react';
import { GadgetProduct } from './NexusSmartGadgetMarketplace';

interface SmartGadgetCardProps {
  gadget: GadgetProduct;
  isAdmin?: boolean;
  isSaved?: boolean;
  isCompared?: boolean;
  onToggleWatchlist?: (gadget: GadgetProduct) => void;
  onToggleCompare?: (gadget: GadgetProduct) => void;
  onSelectGadget?: (gadget: GadgetProduct) => void;
  onEditGadget?: (gadget: GadgetProduct) => void;
  onDeleteGadget?: (id: string, title: string) => void;
  onTagClick?: (tag: string) => void;
}

export const SmartGadgetCard: React.FC<SmartGadgetCardProps> = ({
  gadget,
  isAdmin = false,
  isSaved = false,
  isCompared = false,
  onToggleWatchlist,
  onToggleCompare,
  onSelectGadget,
  onEditGadget,
  onDeleteGadget,
  onTagClick,
}) => {
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);

  // Parse video urls
  const getEmbedInfo = (url?: string): { type: 'youtube' | 'tiktok' | 'none'; embedUrl: string } => {
    if (!url) return { type: 'none', embedUrl: '' };

    // YouTube regex
    const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const ytMatch = url.match(ytRegex);
    if (ytMatch && ytMatch[1]) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?autoplay=1&rel=0&modestbranding=1`,
      };
    }

    // TikTok regex
    if (url.includes('tiktok.com')) {
      const tiktokRegex = /video\/(\d+)/;
      const ttMatch = url.match(tiktokRegex);
      if (ttMatch && ttMatch[1]) {
        return {
          type: 'tiktok',
          embedUrl: `https://www.tiktok.com/embed/v2/${ttMatch[1]}`,
        };
      }
      return {
        type: 'tiktok',
        embedUrl: url, // fallback
      };
    }

    return { type: 'none', embedUrl: '' };
  };

  const { type: videoType, embedUrl } = getEmbedInfo(gadget.sourceVideoUrl || gadget.sourceVideoId);

  return (
    <div
      id={`smart-gadget-card-${gadget.id}`}
      className="group relative flex flex-col justify-between rounded-xl overflow-hidden bg-[#0a0518]/90 border border-purple-500/20 hover:border-cyan-400/40 hover:shadow-[0_0_20px_rgba(232,64,243,0.08)] transition-all duration-300"
    >
      <div className="space-y-4">
        {/* MEDIA PREVIEW CARD */}
        <div className="relative aspect-video bg-black overflow-hidden border-b border-zinc-900/40">
          {isPlayingVideo && embedUrl ? (
            <div className="w-full h-full relative">
              {videoType === 'youtube' ? (
                <iframe
                  src={embedUrl}
                  className="w-full h-full object-cover"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={gadget.title}
                />
              ) : (
                <iframe
                  src={embedUrl}
                  className="w-full h-full object-cover border-none"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={gadget.title}
                />
              )}
              <button
                onClick={() => setIsPlayingVideo(false)}
                className="absolute top-2 right-2 px-2 py-1 bg-black/80 hover:bg-black text-[8px] font-mono font-bold text-pink-400 rounded border border-[#e840f3]/25 z-10"
              >
                COLLAPSE VIDEO
              </button>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={gadget.productImage || "https://images.unsplash.com/photo-1546776310-eef45dd6d63c?w=500"}
                alt={gadget.title}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                onClick={() => onSelectGadget?.(gadget)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
              
              {/* PLAY BUTTON FOR VIDEO ASSETS */}
              {embedUrl ? (
                <button
                  onClick={() => setIsPlayingVideo(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors group-1 animate-pulse"
                >
                  <div className="p-3 bg-[#e840f3]/90 hover:bg-[#e840f3] rounded-full border border-purple-400/40 transform group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(232,64,243,0.5)]">
                    <Video className="w-4 h-4 text-white fill-white" />
                  </div>
                </button>
              ) : (
                <div className="absolute bottom-3 left-3 bg-[#e840f3]/25 border border-[#e840f3]/30 px-2 py-0.5 rounded text-[8px] font-mono text-pink-400 font-black uppercase tracking-wider flex items-center gap-1 pointer-events-none">
                  <Video className="w-3 h-3 text-[#e840f3]" />
                  Video review unavailable
                </div>
              )}
            </div>
          )}

          {/* HOVER BADGES */}
          <div className="absolute top-3 inset-x-3 flex justify-between pointer-events-none select-none z-10">
            <span className="bg-black/95 border border-zinc-850 backdrop-blur px-2 py-0.5 rounded text-[8px] font-mono font-bold text-pink-400 tracking-wider uppercase">
              {gadget.category}
            </span>
            {gadget.sourceChannelName && (
              <span className="bg-black/95 border border-zinc-850 backdrop-blur px-2 py-0.5 rounded text-[8px] font-mono text-cyan-400 font-medium normal-case">
                📹 {gadget.sourceChannelName}
              </span>
            )}
          </div>
        </div>

        {/* DETAILS SECTION */}
        <div className="px-5 space-y-2">
          <div className="flex justify-between items-start gap-2">
            <h3
              onClick={() => onSelectGadget?.(gadget)}
              className="text-sm font-sans font-black text-white hover:text-cyan-300 transition-colors leading-tight uppercase tracking-tight cursor-pointer"
            >
              {gadget.title}
            </h3>
            
            {/* UTILITY SAVE / COMPARE CONTROLS */}
            <div className="flex gap-1 shrink-0 z-10">
              {onToggleCompare && (
                <button
                  onClick={() => onToggleCompare(gadget)}
                  className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                    isCompared
                      ? 'bg-cyan-400/10 border-cyan-400/40 text-cyan-300'
                      : 'border-zinc-850 hover:border-zinc-700 text-slate-500 hover:text-slate-200'
                  }`}
                  title="Add to Compare Matrix"
                >
                  <Layers className="w-3.5 h-3.5" />
                </button>
              )}
              
              {onToggleWatchlist && (
                <button
                  onClick={() => onToggleWatchlist(gadget)}
                  className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                    isSaved
                      ? 'bg-amber-500/10 border-amber-500/40 text-amber-400'
                      : 'border-zinc-850 hover:border-zinc-700 text-slate-500 hover:text-slate-200'
                  }`}
                  title="Save to watchlist"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed font-sans font-medium line-clamp-2">
            {gadget.shortDescription}
          </p>

          {/* INTERACTIVE TAGS */}
          <div className="flex flex-wrap gap-1 pt-1.5 select-none">
            {gadget.tags && gadget.tags.map((tag, tIdx) => (
              <span 
                key={tIdx} 
                onClick={() => onTagClick?.(tag)}
                className="bg-zinc-950/80 hover:bg-[#e840f3]/10 hover:text-[#e840f3] hover:border-[#e840f3]/35 border border-zinc-900/60 px-2 py-0.5 rounded text-[8px] font-mono text-slate-500 uppercase cursor-pointer transition-all"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* FOOTER ACTION PANEL */}
      <div className="p-5 mt-4 border-t border-zinc-950 bg-black/40 relative">
        <div className="flex justify-between items-center mb-3">
          <div className="font-mono">
            <span className="text-[8px] text-zinc-500 uppercase block font-bold leading-none tracking-wider">Estimated Price</span>
            <span className="text-sm font-black text-amber-500 tracking-tight">{gadget.priceText || 'Check Retailer'}</span>
          </div>
          
          <span className="text-[8px] font-mono text-cyan-400 bg-cyan-950/20 px-2.5 py-0.5 rounded border border-cyan-400/30 uppercase tracking-widest font-black">
            {gadget.futureStatus || 'Available'}
          </span>
        </div>

        {/* AFFILIATE DISCLOSURES */}
        {gadget.disclosureLabel && (
          <div className="mb-3 text-[9px] font-mono text-zinc-500 uppercase flex items-center gap-1 select-none">
            <Info size={11} className="text-[#e840f3]" />
            {gadget.disclosureLabel === 'Affiliate link' ? 'Affiliate link • commission eligible' : gadget.disclosureLabel}
          </div>
        )}

        {/* CORE CTA CALL TO ACTIONS */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-[9px] font-mono font-black uppercase tracking-widest select-none">
            <button
              onClick={() => {
                onSelectGadget?.(gadget);
              }}
              className="py-2.5 text-center bg-gradient-to-r from-purple-950/40 to-purple-800/30 hover:to-[#e840f3]/25 border border-purple-500/35 rounded-xl text-white cursor-pointer select-none"
            >
              Watch Review
            </button>
            
            <button
              onClick={() => {
                onSelectGadget?.(gadget);
              }}
              className="py-2.5 text-center bg-black/80 hover:bg-cyan-500/10 border border-cyan-500/32 rounded-xl text-cyan-400 hover:text-white cursor-pointer flex items-center justify-center gap-1"
            >
              Open Deal
            </button>
          </div>

          {/* ADMIN MANAGEMENT HUD OVERLAYS */}
          {isAdmin && (
            <div className="flex gap-1.5 pt-1.5 border-t border-zinc-900/90 justify-end font-mono text-[8px] uppercase font-bold">
              <span className="text-zinc-500 mr-auto self-center select-none text-[7.5px]">ADMIN CONFIG:</span>
              <button
                onClick={() => onEditGadget?.(gadget)}
                className="px-2 py-1 bg-purple-950/50 text-[#e840f3] border border-purple-900/30 rounded flex items-center gap-1 hover:border-[#e840f3]/45"
              >
                <Edit3 size={9} /> Edit
              </button>
              <button
                onClick={() => onDeleteGadget?.(gadget.id, gadget.title)}
                className="p-1 text-slate-500 hover:text-rose-400 hover:bg-rose-950/20 rounded cursor-pointer border border-transparent"
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
